import {cbarConfirm, cbarAlert} from './cbar-message-dialog';
import {
    asCbarDialog,
    bindDialogDismiss,
    closeCbarDialog,
    openCbarDialog,
    type CbarDialogElement,
} from './cbar-dialog-helpers';
import {ensureFormCsrfToken} from './csrf';
import {fetchJson, getElementById} from './dom';

type UploadPermissionResponse = {
    result: string;
    message?: string;
    data?: {
        entity_name: string;
        entity_title: string;
    };
};

type RemoveEntityFileResponse = {
    result: string;
    message?: string;
};

let pendingRemoveEfeId = '';

function getUploadDialog(): CbarDialogElement | null {
    return asCbarDialog(getElementById('modal-upload-files'));
}

function getRemoveDocumentDialog(): CbarDialogElement | null {
    return asCbarDialog(getElementById('modal-remove-document'));
}

function showUploadDialog(): void {
    openCbarDialog(getUploadDialog());
}

function hideUploadDialog(): void {
    closeCbarDialog(getUploadDialog());
}

function showRemoveDocumentDialog(): void {
    openCbarDialog(getRemoveDocumentDialog());
}

function hideRemoveDocumentDialog(): void {
    pendingRemoveEfeId = '';
    closeCbarDialog(getRemoveDocumentDialog());
}

function submitUploadForm(): void {
    const form = getElementById<HTMLFormElement>('form_upload_document');
    if (!form) {
        return;
    }

    ensureFormCsrfToken(form);
    form.requestSubmit();
}

function bindUploadDialogDismiss(): void {
    const dialog = getUploadDialog();
    if (!dialog) {
        return;
    }

    bindDialogDismiss(dialog);
}

function bindRemoveDocumentDialogDismiss(): void {
    const dialog = getRemoveDocumentDialog();
    if (!dialog) {
        return;
    }

    bindDialogDismiss(dialog);
    dialog.addEventListener('close', () => {
        pendingRemoveEfeId = '';
    });
}

function elementFromComposedPath(event: Event, className: string): HTMLElement | null {
    const path = event.composedPath?.() ?? [event.target as EventTarget];
    for (const node of path) {
        const el = node as Element;
        if (el?.classList?.contains(className)) {
            return el as HTMLElement;
        }
    }
    return null;
}

function uploadButtonFromEvent(event: Event): HTMLElement | null {
    return elementFromComposedPath(event, 'upload_file_btn');
}

function removeButtonFromEvent(event: Event): HTMLElement | null {
    return elementFromComposedPath(event, 'remove_efe_btn');
}

function openUploadDialogForButton(button: HTMLElement): void {
    const entityType = button.getAttribute('entity_type') ?? '';
    const entityId = button.getAttribute('entity_id') ?? '';

    void fetchJson<UploadPermissionResponse>('/ajax/getuploadfilepermission', {
        entity_type: entityType,
        entity_id: entityId,
    }).then((response) => {
        if (response.result !== 'success' || !response.data) {
            swal('Cannot upload files', response.message ?? '', 'error');
            return;
        }

        const entityName = getElementById('upload_entity_name');
        const elementTitle = getElementById('upload_element_title');
        if (entityName) {
            entityName.textContent = response.data.entity_name;
        }
        if (elementTitle) {
            elementTitle.innerHTML = response.data.entity_title;
        }

        getElementById<HTMLInputElement>('upload_entity_type')!.value = entityType;
        getElementById<HTMLInputElement>('upload_entity_id')!.value = entityId;

        const fileInput = getElementById<HTMLInputElement>('document_file');
        if (fileInput) {
            fileInput.value = '';
        }

        showUploadDialog();
    });
}

function requestRemoveDocument(button: HTMLElement): void {
    const efeId = button.getAttribute('efe_id') ?? '';
    if (!efeId) {
        return;
    }

    pendingRemoveEfeId = efeId;
    showRemoveDocumentDialog();
}

function confirmRemoveDocument(): void {
    const efeId = pendingRemoveEfeId;
    if (!efeId) {
        hideRemoveDocumentDialog();
        return;
    }

    void fetchJson<RemoveEntityFileResponse>('/ajax/removeentityfileentity', {
        efe_id: efeId,
    }).then((response) => {
        if (response.result === 'success') {
            document.getElementById(`efe_id_${efeId}`)?.remove();
            hideRemoveDocumentDialog();
            return;
        }

        hideRemoveDocumentDialog();
        swal('Cannot remove document', response.message ?? '', 'error');
    });
}

function moveDialogToBody(dialog: CbarDialogElement | null): void {
    if (dialog && dialog.parentElement !== document.body) {
        document.body.appendChild(dialog);
    }
}

export function setupUploadFilesModal(): void {
    const dialog = getUploadDialog();
    if (!dialog) {
        return;
    }

    moveDialogToBody(dialog);
    moveDialogToBody(getRemoveDocumentDialog());

    dialog.open = false;
    const removeDialog = getRemoveDocumentDialog();
    if (removeDialog) {
        removeDialog.open = false;
    }

    void customElements.whenDefined('cbar-dialog').then(() => {
        bindUploadDialogDismiss();
        bindRemoveDocumentDialogDismiss();

        document.addEventListener('click', (event) => {
            const uploadButton = uploadButtonFromEvent(event);
            if (uploadButton) {
                event.preventDefault();
                openUploadDialogForButton(uploadButton);
                return;
            }

            const removeButton = removeButtonFromEvent(event);
            if (removeButton) {
                event.preventDefault();
                event.stopPropagation();
                requestRemoveDocument(removeButton);
            }
        });

        getElementById('upload_file_submit_btn')?.addEventListener('click', () => {
            const fileInput = getElementById<HTMLInputElement>('document_file');
            if (!fileInput?.value) {
                void cbarAlert('Please select a file');
                return;
            }
            void cbarConfirm('Do you want to upload this document?').then((ok) => {
                if (ok) {
                    submitUploadForm();
                }
            });
        });

        getElementById('remove_document_confirm_btn')?.addEventListener('click', (event) => {
            event.preventDefault();
            confirmRemoveDocument();
        });
    });
}
